/**
 * PaymentsLogCard — "תשלומים שהתקבלו": the clinic's payment records
 * (date · amount before VAT · month · method · note) + manual add + delete.
 *
 * Amounts are entered as paid (gross/net per the CRM's VAT flag) and shown
 * BEFORE VAT (the dashboard-wide convention); the gross figure sits in a
 * sub-line. After adding a payment to a one-time deal the card offers to
 * extend "פעיל עד" (CK-9866 flow kept).
 */

import { useEffect, useRef, useState } from "react";
import { Plus, Receipt, Trash } from "@phosphor-icons/react";
import DatePicker from "../../../../../components/ui/DatePicker";
import ConfirmDialog from "../../../../../components/ui/ConfirmDialog";
import HoverTip from "../../../../../components/ui/HoverTip";
import { useToast } from "../../../../../contexts/ToastContext";
import {
  useAddNivCrmPaymentAndExtendMutation,
  useDeleteNivCrmPaymentMutation,
} from "../../../../../store/services/nivApi";
import {
  formatDate,
  formatShekels,
  formatMonthKey,
  getIsraelYmd,
  removeVat,
  suggestPlanEndAfterPayment,
  sumClinicRevenue,
  toMonthKey,
  PAYMENT_METHOD_LABELS,
} from "../nivDerived";
import { CardHead } from "./paymentUi";
import "../ClinicPaymentPanel.css";

/** Last 13 Israel months (newest first) for the manual-add month select. */
const buildMonthOptions = () => {
  const { year, month } = getIsraelYmd();
  const opts = [];
  let y = year;
  let m = month;
  for (let i = 0; i < 13; i++) {
    const key = toMonthKey(y, m);
    opts.push({ value: key, label: formatMonthKey(key) });
    m -= 1;
    if (m < 1) {
      m = 12;
      y -= 1;
    }
  }
  return opts;
};

const paymentPreVat = (p, crm) => {
  if (p?.amountBeforeVat != null) return Number(p.amountBeforeVat) || 0;
  const amount = Number(p?.amount) || 0;
  return crm?.vatIncluded === false ? amount : removeVat(amount);
};

function PaymentsLogCard({ clinic, crm, focusPaymentToken }) {
  const { showSuccess, showError } = useToast();
  const clinicId = clinic?.clinicId;
  const [addPayment, { isLoading: isAdding }] = useAddNivCrmPaymentAndExtendMutation();
  const [deletePayment, { isLoading: isDeleting }] = useDeleteNivCrmPaymentMutation();

  const [payDate, setPayDate] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("");
  const [payMonth, setPayMonth] = useState("");
  const [payNote, setPayNote] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [newEndDate, setNewEndDate] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const amountRef = useRef(null);
  const sectionRef = useRef(null);
  const monthOptions = useRef(buildMonthOptions()).current;

  const reset = () => {
    setPayDate("");
    setPayAmount("");
    setPayMethod("");
    setPayMonth("");
    setPayNote("");
    setConfirmOpen(false);
    setNewEndDate("");
    setDeleteId(null);
  };

  useEffect(() => {
    reset();
  }, [clinicId]);

  useEffect(() => {
    if (!focusPaymentToken) return undefined;
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    const t = setTimeout(() => amountRef.current?.focus(), 250);
    return () => clearTimeout(t);
  }, [focusPaymentToken]);

  if (!clinic) return null;

  const isOneTime = crm?.paymentStatus === "paying" && crm?.billingCycle === "one_time";
  const payments = [...(crm?.payments || [])].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  const revenue = sumClinicRevenue(crm);

  // One-time deals: adding a payment offers to extend "פעיל עד"; everything
  // else just logs the record.
  const handleAdd = () => {
    if (!payDate || !payAmount) {
      showError("יש למלא תאריך וסכום");
      return;
    }
    if (isOneTime) {
      setNewEndDate(suggestPlanEndAfterPayment(clinic.planEndDate, payDate));
      setConfirmOpen(true);
    } else {
      commit(false);
    }
  };

  const commit = async (withDate) => {
    try {
      const amount = Number(payAmount);
      const result = await addPayment({
        clinicId,
        payment: {
          date: payDate,
          amount,
          note: payNote.trim() || null,
          method: payMethod || null,
          month: payMonth || null,
          amountBeforeVat: crm?.vatIncluded === false ? amount : Math.round(removeVat(amount) * 100) / 100,
        },
        newPlanEndDate: withDate && newEndDate ? newEndDate : null,
      }).unwrap();
      reset();
      if (result?.planEndDateError) {
        showError('התשלום נשמר, אך עדכון "פעיל עד" נכשל — עדכן ידנית בסטטוס התשלום');
      } else if (result?.planEndDateUpdated) {
        showSuccess('התשלום נוסף ו"פעיל עד" עודכן');
      } else {
        showSuccess("התשלום נוסף");
      }
    } catch (error) {
      console.error("[CK-9866] Error adding clinic CRM payment:", {
        errorCode: "CK-9866",
        errorMessage: error?.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בהוספת התשלום");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deletePayment({ clinicId, paymentId: deleteId }).unwrap();
      setDeleteId(null);
      showSuccess("התשלום נמחק");
    } catch (error) {
      console.error("[CK-9861] Error deleting clinic CRM payment:", {
        errorCode: "CK-9861",
        errorMessage: error?.message,
        clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה במחיקת התשלום");
    }
  };

  return (
    <section className="ncpp-card" ref={sectionRef}>
      <CardHead
        icon={Receipt}
        eyebrow="יומן"
        title={
          <>
            תשלומים שהתקבלו
            {payments.length > 0 && <span className="ncpp-count">{payments.length}</span>}
          </>
        }
      >
        {revenue.count > 0 && (
          <HoverTip text={`${formatShekels(revenue.gross)} כולל מע"מ`}>
            <span className="ncpp-planend">סה"כ {formatShekels(revenue.preVat)} לפני מע"מ</span>
          </HoverTip>
        )}
      </CardHead>

      {payments.length === 0 ? (
        <div className="ncpp-empty">לא נרשמו תשלומים</div>
      ) : (
        <div className="ncpp-rows">
          {payments.map((p) => (
            <div key={p.id} className="ncpp-row">
              <span className="ncpp-row-date">{formatDate(p.date)}</span>
              <strong className="ncpp-row-amount">{formatShekels(paymentPreVat(p, crm))}</strong>
              <span className="ncpp-row-sub">כולל מע"מ {formatShekels(p.amount)}</span>
              {p.month && <span className="ncpp-badge ncpp-badge-month">{formatMonthKey(p.month)}</span>}
              {p.method && (
                <span className="ncpp-badge ncpp-badge-method">{PAYMENT_METHOD_LABELS[p.method] || p.method}</span>
              )}
              {p.note && <span className="ncpp-row-note">{p.note}</span>}
              <HoverTip text="מחק תשלום">
                <button
                  type="button"
                  className="ncpp-icon-btn is-danger"
                  onClick={() => setDeleteId(p.id)}
                  disabled={isDeleting}
                  aria-label="מחק תשלום"
                >
                  <Trash size={14} />
                </button>
              </HoverTip>
            </div>
          ))}
        </div>
      )}

      {!confirmOpen && (
        <div className="ncpp-payment-add">
          <DatePicker value={payDate} onChange={(e) => setPayDate(e.target.value)} placeholder="תאריך" />
          <input
            ref={amountRef}
            type="number"
            min="0"
            inputMode="numeric"
            className="ncpp-add-amount"
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
            placeholder={crm?.vatIncluded === false ? 'סכום לפני מע"מ' : 'סכום כולל מע"מ'}
          />
          <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)} aria-label="אמצעי תשלום">
            <option value="">אמצעי…</option>
            {Object.entries(PAYMENT_METHOD_LABELS).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
          <select value={payMonth} onChange={(e) => setPayMonth(e.target.value)} aria-label="עבור חודש">
            <option value="">עבור חודש…</option>
            {monthOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="ncpp-add-note"
            value={payNote}
            onChange={(e) => setPayNote(e.target.value)}
            placeholder="הערה (לא חובה)"
          />
          <button type="button" className="ncpp-btn ncpp-btn-secondary" onClick={handleAdd} disabled={isAdding}>
            <Plus size={14} />
            הוסף
          </button>
        </div>
      )}

      {confirmOpen && (
        <div className="ncpp-inline-panel">
          <div className="ncpp-inline-panel-title">
            תשלום של {formatShekels(Number(payAmount))} בתאריך {formatDate(payDate)}
            {payMonth ? ` · ${formatMonthKey(payMonth)}` : ""}
          </div>
          <label className="ncpp-field">
            <span className="ncpp-field-label">עדכן "פעיל עד" ל־</span>
            <DatePicker value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} placeholder="תאריך סיום חדש" />
          </label>
          <div className="ncpp-inline-panel-actions">
            <button type="button" className="ncpp-btn ncpp-btn-primary" onClick={() => commit(true)} disabled={isAdding || !newEndDate}>
              {isAdding ? "שומר…" : "שמור תשלום ועדכן תאריך"}
            </button>
            <button type="button" className="ncpp-btn ncpp-btn-secondary" onClick={() => commit(false)} disabled={isAdding}>
              שמור תשלום בלבד
            </button>
            <button type="button" className="ncpp-link-btn" onClick={() => setConfirmOpen(false)} disabled={isAdding}>
              ביטול
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="מחיקת תשלום"
        message="האם למחוק את רשומת התשלום? פעולה זו אינה הפיכה."
        confirmText="מחק"
        type="danger"
        icon="delete"
      />
    </section>
  );
}

export default PaymentsLogCard;
